/**
 * Apparition douce au défilement des pages produit « à la Apple »
 * (refonte /dashboard, Karim 2026-10-18).
 *
 * Chaque élément `.pa-reveal` sous `racine` monte de 28 px en fondu quand il
 * entre à l'écran. Le rendu serveur reste VISIBLE (sans JavaScript, robots,
 * réseau lent : rien n'est caché) ; on ne masque, au montage, QUE ce qui est
 * encore sous la ligne de flottaison — ce qui est déjà à l'écran ne clignote
 * pas. Le haut de page s'anime en CSS pur (keyframes), dès le premier rendu.
 *
 * `prefers-reduced-motion: reduce` → rien n'est masqué ni animé.
 * Retard par élément : `style="--d: 100ms"`. Styles : `[data-pa]` dans
 * main.css (communs à toutes les pages).
 *
 * Un ATTRIBUT data-pa, pas une classe : les blocs chargés en différé
 * (Lazy… hydrate-on-visible) s'hydratent après ce montage, et Vue compare
 * leur `class` au rendu serveur (« Hydration class mismatch »). Un attribut
 * absent du gabarit n'est ni comparé ni écrasé.
 */
export function useApparitionAuDefilement(racine: Ref<HTMLElement | null>) {
  let observateur: IntersectionObserver | null = null;

  onMounted(() => {
    const el = racine.value;
    if (!el || !("IntersectionObserver" in window)) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ligneFlottaison = window.innerHeight * 0.92;
    const cibles = Array.from(el.querySelectorAll<HTMLElement>(".pa-reveal")).filter(
      (c) => c.getBoundingClientRect().top > ligneFlottaison,
    );
    if (cibles.length === 0) return;

    const enAttente = new Set(cibles);
    function montrer(cible: HTMLElement) {
      enAttente.delete(cible);
      observateur?.unobserve(cible);
      cible.dataset.pa = "vu";
      // Fin du fondu : on retire l'attribut, l'élément retrouve ses propres
      // transitions (effets de survol des cartes).
      const retard = Number.parseFloat(getComputedStyle(cible).transitionDelay) * 1000 || 0;
      setTimeout(() => delete cible.dataset.pa, retard + 1000);
    }

    observateur = new IntersectionObserver(
      (entrees) => {
        for (const entree of entrees) {
          if (entree.isIntersecting) montrer(entree.target as HTMLElement);
        }
        // Défilement très rapide (molette, touche Fin, ancre) : un élément
        // peut traverser l'écran entre deux images sans être « vu ». Tout ce
        // qui est déjà au-dessus du bas de l'écran s'affiche aussi.
        for (const cible of enAttente) {
          if (cible.getBoundingClientRect().top < window.innerHeight) montrer(cible);
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.12 },
    );
    for (const c of cibles) {
      c.dataset.pa = "cache";
      observateur.observe(c);
    }
  });

  onBeforeUnmount(() => observateur?.disconnect());
}
